import {Client, StompSubscription} from "@stomp/stompjs";

interface Subscription {
    destination: string;
    callback: (event: unknown) => void;
    stompSubscription?: StompSubscription;
}

export interface RealtimeClient {
    connect(): void;
    disconnect(): Promise<void>;
    subscribe<T>(destination: string, callback: (event: T) => void): () => void;
    publish<T>(destination: string, body: T): void;
    onConnected(callback: () => void): () => void;
}

class StompRealtimeClient implements RealtimeClient {
    private readonly client: Client;
    private readonly subscriptions = new Set<Subscription>();
    private readonly connectionListeners = new Set<() => void>();
    private desiredConnected = false;
    private disconnecting?: Promise<void>;

    constructor(brokerURL: string) {
        this.client = new Client({
            brokerURL,
            reconnectDelay: 5000,
            heartbeatIncoming: 10000,
            heartbeatOutgoing: 10000,
            onConnect: () => {
                this.restoreSubscriptions();
                this.connectionListeners.forEach(listener => listener());
            },
            onWebSocketClose: () => {
                this.subscriptions.forEach(subscription => subscription.stompSubscription = undefined);
            },
        });
    }

    connect() {
        this.desiredConnected = true;
        if (this.disconnecting) {
            void this.disconnecting.then(() => {
                if (this.desiredConnected) this.client.activate();
            });
            return;
        }
        this.client.activate();
    }

    async disconnect(): Promise<void> {
        this.desiredConnected = false;
        const operation = this.client.deactivate();
        this.disconnecting = operation;
        try {
            await operation;
        } finally {
            if (this.disconnecting === operation) this.disconnecting = undefined;
        }
    }

    subscribe<T>(destination: string, callback: (event: T) => void): () => void {
        const subscription: Subscription = {
            destination: `/topic${destination}`,
            callback: event => callback(event as T),
        };
        this.subscriptions.add(subscription);
        this.activateSubscription(subscription);
        return () => {
            subscription.stompSubscription?.unsubscribe();
            this.subscriptions.delete(subscription);
        };
    }

    publish<T>(destination: string, body: T): void {
        if (!this.client.connected) throw new Error("STOMP client is not connected");
        this.client.publish({destination: `/app${destination}`, body: JSON.stringify(body)});
    }

    onConnected(callback: () => void): () => void {
        this.connectionListeners.add(callback);
        return () => this.connectionListeners.delete(callback);
    }

    private activateSubscription(subscription: Subscription) {
        if (!this.client.connected) return;
        subscription.stompSubscription = this.client.subscribe(
            subscription.destination,
            message => subscription.callback(JSON.parse(message.body) as unknown),
        );
    }

    private restoreSubscriptions() {
        for (const subscription of this.subscriptions) this.activateSubscription(subscription);
    }
}

export function createRealtimeClient(): RealtimeClient {
    const protocol = window.location.protocol === "https:" ? "wss" : "ws";
    return new StompRealtimeClient(`${protocol}://${window.location.host}/api/ws`);
}
